/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaRunNextBar.tsx
 * FILE_ID: JOBHUB-QA-RUN-NEXT-BAR-001
 * PURPOSE: End-of-checklist Area / ITP / GO bar. Selection changes do not enter Run.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useIsMdUp } from '@quotech/ui'
import { cn } from '@quotech/tenant-shared/lib/utils'
import type { QaApi } from '../../qa/qaApi'
import { checklistGroups } from '../../qa/qaApiHelpers'
import { QaPhoneScopeSheet } from './phone/sheets/QaPhoneScopeSheet'
import { useQaItpExecutions } from './useQaItpExecutions'
import {
  stepSelection,
  suggestNextRun,
  type RunArea,
  type RunCatalog,
  type RunChecklist,
} from './qaRunNext'

export type AreaPick = { id: string; nonce: number }

type Selection = { areaId: string; checklistId: string | null }

function scrollRunToTop(from: HTMLElement | null) {
  if (!from || typeof document === 'undefined') return
  const phone = document.querySelector('[data-testid="qa-phone-scroll"]')
  if (phone instanceof HTMLElement && phone.contains(from)) {
    phone.scrollTop = 0
    return
  }
  let node: HTMLElement | null = from.parentElement
  while (node) {
    const overflow = getComputedStyle(node).overflowY
    if (overflow === 'auto' || overflow === 'scroll') {
      node.scrollTop = 0
      return
    }
    node = node.parentElement
  }
}

export function QaRunNextBar({
  scopeAreas,
  viewedAreaId,
  viewedChecklistId,
  currentChecklists,
  api,
  onGo,
  onOpenAreaPicker,
  areaPick = null,
  onShownArea,
}: {
  scopeAreas: RunArea[]
  viewedAreaId: string
  viewedChecklistId: string | null
  currentChecklists: RunChecklist[]
  api: Pick<QaApi, 'getAreaExecution'>
  onGo: (areaId: string, checklistId: string | null) => void
  onOpenAreaPicker?: () => void
  areaPick?: AreaPick | null
  onShownArea?: (id: string) => void
}) {
  const isDesktop = useIsMdUp()
  const rootRef = useRef<HTMLDivElement>(null)
  const seenNonce = useRef(0)
  const areaIds = scopeAreas.map((row) => row.id)
  const ready = typeof api.getAreaExecution === 'function'
  const { executions } = useQaItpExecutions(areaIds, api, ready)
  const catalog = useMemo<RunCatalog>(() => {
    const next: RunCatalog = {}
    for (const area of executions) {
      next[area.id] = checklistGroups(area).map((group) => ({
        id: group.checklist.id,
        name: group.name,
      }))
    }
    next[viewedAreaId] = currentChecklists
    return next
  }, [currentChecklists, executions, viewedAreaId])
  const suggestion = useMemo(() => suggestNextRun({
    areaIds,
    catalog,
    viewedAreaId,
    viewedChecklistId,
    currentChecklists,
  }), [areaIds, catalog, currentChecklists, viewedAreaId, viewedChecklistId])
  const viewKey = `${viewedAreaId}:${viewedChecklistId ?? ''}`
  const [selection, setSelection] = useState<Selection>({
    areaId: suggestion.areaId,
    checklistId: suggestion.checklistId,
  })
  const [touched, setTouched] = useState(false)
  const [openList, setOpenList] = useState<'area' | 'checklist' | null>(null)
  const [checklistOpen, setChecklistOpen] = useState(false)
  const [selectingChecks, setSelectingChecks] = useState(false)

  useEffect(() => {
    setTouched(false)
    setSelection({ areaId: suggestion.areaId, checklistId: suggestion.checklistId })
    // The suggestion for a new checklist is applied again below once its catalog arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewKey])

  useEffect(() => {
    if (touched) return
    setSelection({ areaId: suggestion.areaId, checklistId: suggestion.checklistId })
  }, [suggestion.areaId, suggestion.checklistId, suggestion.pending, touched])

  useEffect(() => {
    if (!touched || selection.checklistId) return
    const list = catalog[selection.areaId]
    if (!list?.length) return
    setSelection((current) => (
      current.checklistId ? current : { ...current, checklistId: list[0].id }
    ))
  }, [catalog, selection.areaId, selection.checklistId, touched])

  useEffect(() => {
    if (!areaPick || areaPick.nonce === seenNonce.current) return
    seenNonce.current = areaPick.nonce
    const list = catalog[areaPick.id]
    setTouched(true)
    setSelection({
      areaId: areaPick.id,
      checklistId: list?.length ? list[0].id : null,
    })
  }, [areaPick, catalog])

  useEffect(() => {
    if (selection.areaId) onShownArea?.(selection.areaId)
  }, [onShownArea, selection.areaId])

  if (areaIds.length === 0) return null

  const selectedList = catalog[selection.areaId]
  const listPending = !(selection.areaId in catalog)
  const checkIds = (selectedList ?? []).map((row) => row.id)
  const areaName = scopeAreas.find((row) => row.id === selection.areaId)?.name ?? 'Area'
  const checklist = selectedList?.find((row) => row.id === selection.checklistId)
  const checklistLabel = listPending ? '…' : selectedList?.length ? (checklist?.name ?? '…') : 'None'
  const areaPrev = stepSelection(areaIds, selection.areaId, -1)
  const areaNext = stepSelection(areaIds, selection.areaId, 1)
  const checkPrev = selection.checklistId ? stepSelection(checkIds, selection.checklistId, -1) : null
  const checkNext = selection.checklistId ? stepSelection(checkIds, selection.checklistId, 1) : null

  function chooseArea(areaId: string) {
    const list = catalog[areaId]
    setTouched(true)
    setSelection({ areaId, checklistId: list?.length ? list[0].id : null })
    setOpenList(null)
  }

  function go() {
    if (listPending) return
    onGo(selection.areaId, selectedList?.length ? selection.checklistId : null)
    const node = rootRef.current
    requestAnimationFrame(() => scrollRunToTop(node))
  }

  return (
    <div ref={rootRef} className="border-t border-border px-4 py-3" data-testid="qa-run-next">
      <div className={cn('flex flex-wrap items-center gap-2', !isDesktop && 'flex-col')}>
        <div className={cn('flex min-w-0 items-center rounded-lg border border-border', !isDesktop && 'w-full')}>
          <Arrow label="Previous area" disabled={!areaPrev} onClick={() => areaPrev && chooseArea(areaPrev)}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Arrow>
          <button
            type="button"
            aria-label={areaName}
            className="min-h-11 min-w-0 flex-1 truncate px-2 text-left text-sm"
            onClick={() => {
              if (!isDesktop) onOpenAreaPicker?.()
              else setOpenList((current) => (current === 'area' ? null : 'area'))
            }}
          >
            {areaName}
          </button>
          <Arrow label="Next area" disabled={!areaNext} onClick={() => areaNext && chooseArea(areaNext)}>
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Arrow>
        </div>
        <div className={cn('flex min-w-0 items-center rounded-lg border border-border', !isDesktop && 'w-full')}>
          <Arrow
            label="Previous checklist"
            disabled={!checkPrev}
            onClick={() => {
              if (!checkPrev) return
              setTouched(true)
              setSelection((current) => ({ ...current, checklistId: checkPrev }))
            }}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </Arrow>
          <button
            type="button"
            aria-label={checklistLabel}
            disabled={listPending || !selectedList?.length}
            className="min-h-11 min-w-0 flex-1 truncate px-2 text-left text-sm disabled:opacity-40"
            onClick={() => {
              if (listPending || !selectedList?.length) return
              if (!isDesktop) setChecklistOpen(true)
              else setOpenList((current) => (current === 'checklist' ? null : 'checklist'))
            }}
          >
            {checklistLabel}
          </button>
          <Arrow
            label="Next checklist"
            disabled={!checkNext}
            onClick={() => {
              if (!checkNext) return
              setTouched(true)
              setSelection((current) => ({ ...current, checklistId: checkNext }))
            }}
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </Arrow>
        </div>
        <button
          type="button"
          aria-label="Go"
          disabled={listPending}
          onClick={go}
          className={cn(
            'min-h-[44px] rounded-lg bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-40',
            !isDesktop && 'w-full',
          )}
        >
          GO
        </button>
      </div>
      {isDesktop && openList === 'area' ? (
        <div className="mt-2 max-h-[45vh] overflow-auto rounded-md border border-border">
          {scopeAreas.map((row, index) => {
            const showGroup = row.group && row.group !== scopeAreas[index - 1]?.group
            return (
              <div key={row.id}>
                {showGroup ? (
                  <p className="px-3 pt-2 text-[11px] font-semibold uppercase text-muted-foreground">{row.group}</p>
                ) : null}
                <button
                  type="button"
                  className={cn(
                    'flex min-h-11 w-full items-center truncate px-3 text-left text-sm',
                    row.id === selection.areaId && 'bg-primary/10',
                  )}
                  onClick={() => chooseArea(row.id)}
                >
                  {row.name}
                </button>
              </div>
            )
          })}
        </div>
      ) : null}
      {isDesktop && openList === 'checklist' ? (
        <div className="mt-2 max-h-[45vh] overflow-auto rounded-md border border-border">
          {(selectedList ?? []).map((row) => (
            <button
              key={row.id}
              type="button"
              className={cn(
                'flex min-h-11 w-full items-center truncate px-3 text-left text-sm',
                row.id === selection.checklistId && 'bg-primary/10',
              )}
              onClick={() => {
                setTouched(true)
                setSelection((current) => ({ ...current, checklistId: row.id }))
                setOpenList(null)
              }}
            >
              {row.name}
            </button>
          ))}
        </div>
      ) : null}
      {!isDesktop ? (
        <QaPhoneScopeSheet
          open={checklistOpen}
          onClose={() => setChecklistOpen(false)}
          title="Checklist"
          rows={(selectedList ?? []).map((row) => ({ id: row.id, name: row.name }))}
          currentId={selection.checklistId}
          selecting={selectingChecks}
          selectedIds={new Set()}
          onToggleSelectMode={() => setSelectingChecks((on) => !on)}
          onPick={(id) => {
            setTouched(true)
            setSelection((current) => ({ ...current, checklistId: id }))
            setChecklistOpen(false)
          }}
          onToggle={() => {}}
        />
      ) : null}
    </div>
  )
}

function Arrow({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="flex h-11 w-11 shrink-0 items-center justify-center disabled:opacity-40"
    >
      {children}
    </button>
  )
}
